import { TTypeOfCodec } from "../../../shared/src/codecs/codec";
import { fileIO, TFileIOCodec } from "../../../shared/src/codecs/types/fileIO";
import { required, TRequiredCodec } from "../../../shared/src/codecs/types/required";
import { string, TAnyStringCodec } from "../../../shared/src/codecs/types/string";
import { uuid, TUuidCodec } from "../../../shared/src/codecs/types/uuid";
import { EmptyObject, TEmptyObjectCodec } from "../EmptyObject";

/**
 * Uploads an introducer's brand logo for the co-branded lockup on their partner landing
 * page.
 *
 * The upload writes a fully-qualified public S3 URL to `introducers.brand_logo_url`, not a
 * download token. The referral-agreement upload next to this one produces an authenticated
 * `legal_files` token, which is right for a contract and useless here — the logo is rendered
 * by an anonymous browser on a public marketing page, so it has to be fetchable without a
 * session. That is the same requirement listing photos already have, so this reuses their
 * bucket.
 *
 * The output is empty on purpose. `fileIO.decode` falls back to decoding a row as BOTH the
 * input and the output, which is how `GetIntroducersController` gets away with building this
 * form from a bare `JSONB_BUILD_OBJECT` of the input fields — an output with anything
 * required in it fails that fallback and takes the whole introducer list down with it. The
 * resulting URL is read from the introducer's own `brand_logo_url`, which the form already
 * carries.
 */
export const IntroducerLogoFileIO: TFileIOCodec<
    TRequiredCodec<{
        id: TUuidCodec,
        file_extension: TAnyStringCodec,
        mime_type: TAnyStringCodec,
    }>,
    TEmptyObjectCodec
> = fileIO(
    required({
        id: uuid(),
        file_extension: string(),
        mime_type: string(),
    }),
    EmptyObject,
);

export type TIntroducerLogoFileIOCodec = typeof IntroducerLogoFileIO;
export type TIntroducerLogoFileIO = TTypeOfCodec<TIntroducerLogoFileIOCodec>;

/**
 * What the lockup can render.
 *
 * **SVG is deliberately excluded**, even though most brand assets arrive as one. An SVG is
 * active content: scripts inside it do not execute when it is loaded through `<img>`, which
 * is how the lockup renders it, but they do if anyone opens the object URL directly. The
 * file comes from a third party and lands in a bucket served on a shared AWS domain, so the
 * safe default is to not host it at all. The lockup caps the logo at 34px high, where a
 * raster is indistinguishable from a vector. If vectors are ever needed, the way in is
 * server-side sanitisation, not widening this list.
 */
export const INTRODUCER_LOGO_MIME_TYPES: ReadonlyArray<string> = [
    "image/png",
    "image/jpeg",
    "image/webp",
];
